import { useState, useEffect } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { AdditionalPhotosPicker } from "./AdditionalPhotosPicker";

const MEDIUMS = ["Painting", "Mixed Media", "Sculpture", "Photography", "Digital", "Curios"];

export const ArtworkUploadForm = ({ user, onCreated, onCancel, submitFn }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    medium: "Painting",
    image_url: "",
    year: new Date().getFullYear(),
    dimensions: "",
    country: user?.country || "",
  });
  const [gallery, setGallery] = useState([]);
  const [shipping, setShipping] = useState({
    africa: "",
    europe: "",
    north_america: "",
    asia_oceania: "",
    rest_of_world: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [centerMembership, setCenterMembership] = useState(null);
  const [underCenter, setUnderCenter] = useState(true);

  useEffect(() => {
    if (submitFn) return; // on-behalf uploads: center attribution handled server-side
    (async () => {
      try {
        const { data } = await api.get("/memberships/mine");
        setCenterMembership(data.active || null);
      } catch (_e) {
        /* non-blocking */
      }
    })();
  }, [submitFn]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        year: parseInt(form.year) || null,
        gallery,
        under_center: centerMembership ? underCenter : false,
      };
      const { data } = submitFn ? await submitFn(payload) : await api.post("/artworks", payload);
      onCreated?.(data);
      toast.success("Artwork added to gallery");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add artwork");
    } finally {
      setSubmitting(false);
    }
  };

  let uploadBtnLabel = "Upload from device";
  if (uploading) uploadBtnLabel = "Uploading…";
  else if (form.image_url) uploadBtnLabel = "Replace image";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="add-artwork-form"
      className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 border border-[#E5E0D8] p-8 bg-white"
    >
      <div className="md:col-span-2">
        <label className="label-eyebrow text-[#555]">Title</label>
        <Input
          data-testid="artwork-title-input"
          required
          value={form.title}
          onChange={set("title")}
          className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
        />
      </div>
      <div className="md:col-span-2">
        <label className="label-eyebrow text-[#555]">Description</label>
        <Textarea
          data-testid="artwork-desc-input"
          required
          rows={3}
          value={form.description}
          onChange={set("description")}
          className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
        />
      </div>
      <div className="md:col-span-2">
        <label className="label-eyebrow text-[#555]">Artwork image</label>
        <div className="mt-3 flex flex-col sm:flex-row gap-4 items-start">
          <label
            htmlFor="artwork-image-file"
            data-testid="artwork-image-upload-label"
            className="cursor-pointer rounded-none border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-3 label-eyebrow"
          >
            {uploadBtnLabel}
            <input
              id="artwork-image-file"
              data-testid="artwork-image-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
          {form.image_url ? (
            <div className="flex items-center gap-3">
              <img
                src={form.image_url.startsWith("/api") ? `${BACKEND_URL}${form.image_url}` : form.image_url}
                alt="Preview"
                className="w-20 h-20 object-cover border border-[#E5E0D8]"
              />
              <span className="label-eyebrow text-[#1E3A8A]">Ready</span>
            </div>
          ) : (
            <span className="text-sm text-[#555] self-center">JPEG, PNG, WebP — up to 10MB</span>
          )}
        </div>
        <input type="hidden" value={form.image_url} required data-testid="artwork-image-hidden" readOnly />
      </div>
      <div className="md:col-span-2">
        <label className="label-eyebrow text-[#555]">Additional photos (optional)</label>
        <AdditionalPhotosPicker
          value={gallery}
          onChange={setGallery}
          disabled={submitting}
          testid="upload-gallery"
        />
      </div>
      <div>
        <label className="label-eyebrow text-[#555]">Price (USD)</label>
        <Input
          data-testid="artwork-price-input"
          required
          type="number"
          step="0.01"
          min="1"
          value={form.price}
          onChange={set("price")}
          className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
        />
      </div>
      <div>
        <label className="label-eyebrow text-[#555]">Medium</label>
        <Select value={form.medium} onValueChange={(v) => setForm({ ...form, medium: v })}>
          <SelectTrigger
            data-testid="artwork-medium-input"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {MEDIUMS.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="label-eyebrow text-[#555]">Year</label>
        <Input
          data-testid="artwork-year-input"
          type="number"
          value={form.year}
          onChange={set("year")}
          className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
        />
      </div>
      <div>
        <label className="label-eyebrow text-[#555]">Dimensions</label>
        <Input
          data-testid="artwork-dimensions-input"
          value={form.dimensions}
          onChange={set("dimensions")}
          placeholder="H × W × D in cm  ·  e.g. 140 x 45 x 40 cm"
          className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
        />
        <p className="text-xs text-[#555] mt-1.5" data-testid="artwork-dimensions-hint">
          Sculptures &amp; curios: enter <b>height first</b> in cm — we use it to
          scale the &ldquo;See it in your space&rdquo; visualiser realistically
          (floor, plinth, sideboard or shelf) against a 1.7 m human silhouette.
        </p>
      </div>
      <div className="md:col-span-2 mt-2">
        <p className="label-eyebrow text-[#1E3A8A]">— Shipping per zone (USD)</p>
        <p className="text-xs text-[#555] mt-1">
          Set the price you&apos;ll charge to ship this work. Leave a zone blank to mark it as free.
        </p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { id: "africa", label: "Africa" },
            { id: "europe", label: "Europe & UK" },
            { id: "north_america", label: "North America" },
            { id: "asia_oceania", label: "Asia & Oceania" },
            { id: "rest_of_world", label: "Rest of world" },
          ].map((z) => (
            <div key={z.id}>
              <label className="label-eyebrow text-[#555] text-xs">{z.label}</label>
              <Input
                data-testid={`artwork-ship-${z.id}`}
                type="number"
                min="0"
                step="1"
                value={shipping[z.id]}
                onChange={(e) => setShipping((s) => ({ ...s, [z.id]: e.target.value }))}
                placeholder="0"
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
              />
            </div>
          ))}
        </div>
      </div>
      {centerMembership && (
        <div className="md:col-span-2 border-l-2 border-[#F4C430] bg-[#F4EFE6]/40 px-4 py-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="artwork-under-center-toggle"
              checked={underCenter}
              onChange={(e) => setUnderCenter(e.target.checked)}
              className="w-4 h-4 accent-[#1E3A8A]"
            />
            <span className="text-sm text-[#333]">
              Present this work under{" "}
              <span className="font-serif-display italic text-[#1E3A8A]">{centerMembership.center_name}</span>{" "}
              (your art hub)
            </span>
          </label>
        </div>
      )}
      <div className="md:col-span-2 flex gap-3">
        <Button
          type="submit"
          data-testid="submit-artwork-btn"
          disabled={submitting}
          className="flex-1 rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-6 h-auto label-eyebrow"
        >
          {submitting ? "Publishing…" : "Publish to Gallery"}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="rounded-none border-[#1E3A8A] text-[#1E3A8A] py-6 h-auto label-eyebrow px-6"
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};
